/**
 * Signals traffic you can hear: turns what changes in the player's view into effect cues.
 *  - an order leaving headquarters: morse on the key (courier), a magneto ring (telephone) or a
 *    breath of static (radio) — read from new couriers / dispatches in the view;
 *  - an acknowledgement coming back (`orderDelivered`): the sounder answers in morse.
 * Telegram cards bring their own paper swish and stamp thump (GameScreen).
 */
import type { PlayerView } from '@friction/engine';
import { gameStore } from '../game/store';
import { playSound } from './sfx';

/** What left headquarters between two views: new couriers and new telephone/radio dispatches. */
export function newTraffic(
  prev: PlayerView,
  next: PlayerView,
): { courier: number; telephone: number; radio: number } {
  const known = new Set<number>();
  for (const c of prev.couriers) known.add(c.order.id);
  for (const d of prev.dispatches) known.add(d.order.id);
  const out = { courier: 0, telephone: 0, radio: 0 };
  for (const c of next.couriers) if (!known.has(c.order.id)) out.courier++;
  for (const d of next.dispatches)
    if (!known.has(d.order.id)) out[d.order.channel === 'radio' ? 'radio' : 'telephone']++;
  return out;
}

export function startCues(): () => void {
  return gameStore.subscribe((st, prev) => {
    // Nothing on the first view of a match (the opening state is not news), nor in a replay
    // (every faction's signals traffic at up to 16× would be a din), nor for an observer (the
    // traffic is not ours).
    if (
      !st.view ||
      !prev.view ||
      st.map !== prev.map ||
      st.view === prev.view ||
      st.replay ||
      prev.replay ||
      st.observing !== null
    )
      return;
    const sent = newTraffic(prev.view, st.view);
    if (sent.radio) playSound('static');
    if (sent.telephone) playSound('phone', 0.9);
    if (sent.courier) playSound('morseOut');
    if (st.freshEvents !== prev.freshEvents) {
      const own = new Set(st.view.units.map((u) => u.id));
      if (st.freshEvents.some((e) => e.kind === 'orderDelivered' && own.has(e.unit)))
        window.setTimeout(() => playSound('morse'), sent.courier || sent.telephone ? 450 : 0);
    }
  });
}
